import { useRoomContext } from '@livekit/components-react'
import { RoomEvent, Track, type LocalVideoTrack, type RemoteTrack } from 'livekit-client'
import { useEffect, useRef } from 'react'

import { TICK_MS } from './dataSaver'
import { report } from './diagnostics'
import {
  INITIAL_PUB_QUALITY,
  publishLimited,
  stepPublishQuality,
  videoQualityFor,
  type PubQuality,
} from './publishSaver'

/**
 * Reports the funnel stages the server cannot see. Without these, a class where
 * nobody can hear anything looks identical to a perfect class from the API's
 * point of view — "connected" only means signalling succeeded.
 *
 * - `peer_present`  a remote participant is in the room, so media is now
 *   EXPECTED. Without this, the funnel cannot tell "silent class" from "tutor
 *   waiting alone 15 minutes before the lesson" — and since Cairo start-of-hour
 *   is a wave of early tutors, a no-media alarm built on connections alone would
 *   fire constantly and get muted, losing the only signal that matters.
 * - `first_media`  the first remote track actually arrives (audio or video)
 * - `stable_media` still receiving that track STABLE_AFTER_MS later, which is
 *   what distinguishes "it connected" from "it worked"
 *
 * Rendered inside <LiveKitRoom> so it can use the room context. Reports once
 * each; fire-and-forget.
 */

const STABLE_AFTER_MS = 15_000

/**
 * A deliberately small, app-owned view of the browser's transport facts. It
 * is shared with the data saver so we never make a UI decision from LiveKit's
 * qualitative label alone. Counters are cumulative; callers compare samples.
 */
export interface LinkHealth {
  inboundBytes: number
  inboundAudioBytes: number
  inboundAudioEnergy: number
  inboundVideoPacketsReceived: number
  inboundVideoPacketsLost: number
  inboundVideoFramesDecoded: number
  inboundVideoFramesDropped: number
}

/**
 * Best-effort WebRTC link evidence for the existing five-second saver tick.
 * This intentionally reaches the same LiveKit transport internals as the
 * media reporter: absent browser stats are an unknown link, never a failure.
 */
export async function readLinkHealth(room: unknown): Promise<LinkHealth | undefined> {
  try {
    type Transport = { getStats?: () => Promise<RTCStatsReport> }
    type StatsRow = {
      type?: string
      kind?: string
      mediaType?: string
      bytesReceived?: number
      packetsReceived?: number
      packetsLost?: number
      framesDecoded?: number
      framesDropped?: number
      totalAudioEnergy?: number
    }
    const engine = (room as {
      engine?: { pcManager?: { subscriber?: Transport } }
    }).engine
    const subscriber = engine?.pcManager?.subscriber
    const subscriberStats = await subscriber?.getStats?.call(subscriber)
    if (!subscriberStats) return undefined

    const health: LinkHealth = {
      inboundBytes: 0,
      inboundAudioBytes: 0,
      inboundAudioEnergy: 0,
      inboundVideoPacketsReceived: 0,
      inboundVideoPacketsLost: 0,
      inboundVideoFramesDecoded: 0,
      inboundVideoFramesDropped: 0,
    }
    subscriberStats?.forEach((entry) => {
      const row = entry as StatsRow
      if (row.type !== 'inbound-rtp') return
      if (typeof row.bytesReceived === 'number') health.inboundBytes += row.bytesReceived
      if (row.kind === 'audio' || row.mediaType === 'audio') {
        if (typeof row.bytesReceived === 'number') health.inboundAudioBytes += row.bytesReceived
        if (typeof row.totalAudioEnergy === 'number') health.inboundAudioEnergy += row.totalAudioEnergy
      }
      if (row.kind !== 'video' && row.mediaType !== 'video') return
      if (typeof row.packetsReceived === 'number') health.inboundVideoPacketsReceived += row.packetsReceived
      if (typeof row.packetsLost === 'number') health.inboundVideoPacketsLost += row.packetsLost
      if (typeof row.framesDecoded === 'number') health.inboundVideoFramesDecoded += row.framesDecoded
      if (typeof row.framesDropped === 'number') health.inboundVideoFramesDropped += row.framesDropped
    })
    return health
  } catch {
    return undefined
  }
}

interface Props {
  credential: { code?: string | null; reconnectToken?: string | null }
  isHost: boolean
}

function usePublishSaver(room: ReturnType<typeof useRoomContext>, isHost: boolean) {
  const stateRef = useRef<{
    track: LocalVideoTrack
    current: PubQuality
    healthyTicks: number
    limitedTicks: number
  } | null>(null)
  const samplingRef = useRef(false)

  useEffect(() => {
    if (!room || !isHost) return
    let cancelled = false

    const sample = async () => {
      if (samplingRef.current) return
      const camera = room.localParticipant.getTrackPublication(Track.Source.Camera)?.track as
        | LocalVideoTrack
        | undefined
      if (!camera || camera.isMuted) return

      samplingRef.current = true
      try {
        const stats = await camera.getSenderStats()
        const currentCamera = room.localParticipant.getTrackPublication(Track.Source.Camera)?.track
        if (cancelled || currentCamera !== camera || camera.isMuted) return
        if (stats.length === 0) return // no sender evidence is not a healthy sample

        const previous = stateRef.current?.track === camera
          ? stateRef.current
          : { track: camera, current: INITIAL_PUB_QUALITY, healthyTicks: 0, limitedTicks: 0 }
        const next = stepPublishQuality({
          current: previous.current,
          limited: publishLimited(stats),
          healthyTicks: previous.healthyTicks,
          limitedTicks: previous.limitedTicks,
        })
        stateRef.current = {
          track: camera,
          current: next.next,
          healthyTicks: next.healthyTicks,
          limitedTicks: next.limitedTicks,
        }

        // Write encodings only on a step. A new track already publishes every
        // layer at INITIAL_PUB_QUALITY; re-asserting it would override dynacast.
        if (next.next !== previous.current) {
          camera.setPublishingQuality(videoQualityFor(next.next))
        }
      } catch {
        // Stats are advisory; a browser that cannot expose them stays at its current quality.
      } finally {
        samplingRef.current = false
      }
    }

    void sample()
    const timer = window.setInterval(() => void sample(), TICK_MS)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [isHost, room])
}

export function MediaReporter({ credential, isHost }: Props) {
  const room = useRoomContext()
  const reportedFirst = useRef(false)
  const reportedStable = useRef(false)
  const reportedPeer = useRef(false)

  usePublishSaver(room, isHost)

  useEffect(() => {
    if (!room) return

    const connectedAt = Date.now()
    let stableTimer: ReturnType<typeof setTimeout> | undefined

    // Reported once, as soon as anyone else is in the room. This is the honest
    // denominator for "did media flow": a participant with no peer SHOULD see no
    // remote media.
    const reportPeer = () => {
      if (reportedPeer.current) return
      if (room.remoteParticipants.size === 0) return
      reportedPeer.current = true
      report(credential, { stage: 'peer_present', durationMs: Date.now() - connectedAt })
    }

    const onSubscribed = (track: RemoteTrack) => {
      reportPeer()
      if (reportedFirst.current) return
      reportedFirst.current = true

      // Which path media took matters most on Egyptian school and office
      // networks, where UDP is often blocked outright and the class only
      // survives because it fell back to TURN over TCP/443. That fallback is
      // slower and pricier, so we need to know how often it happens — and
      // "media failed" from a UDP block has to be distinguishable from "media
      // failed" from a starved CPU.
      //
      // Read asynchronously (getStats returns a promise) and reported when it
      // resolves, so first_media itself is never delayed by it.
      //
      // The timestamp is taken HERE, not inside the `then`: media arrived now, and
      // measuring after getStats resolves would fold stats latency into every
      // first_media duration — quietly corrupting the "is TURN slower than a
      // direct path?" comparison that reading the ICE type is for.
      const firstMediaAt = Date.now()
      void describeIce(room).then((ice) => {
        report(credential, {
          stage: 'first_media',
          durationMs: firstMediaAt - connectedAt,
          ice,
        })
      })

      // readyState stays "live" when packets simply stop arriving, so it cannot
      // tell a working stream from a frozen one. Compare received bytes across
      // the interval instead — that is the only thing that proves media flowed.
      void bytesReceived(room).then((before) => {
        stableTimer = setTimeout(() => {
          void bytesReceived(room).then((after) => {
            if (reportedStable.current) return
            reportedStable.current = true

            const grew = after !== undefined && before !== undefined ? after > before : undefined
            const alive = track.mediaStreamTrack?.readyState === 'live'
            // Unknown byte counts fall back to readyState rather than crying wolf.
            const healthy = grew ?? alive

            report(credential, {
              stage: 'stable_media',
              outcome: healthy ? 'ok' : 'error',
              errorKind: healthy ? undefined : grew === false ? 'media_frozen' : 'media_ended_early',
              durationMs: Date.now() - connectedAt,
            })
          })
        }, STABLE_AFTER_MS)
      })
    }

    reportPeer()
    room.on(RoomEvent.ParticipantConnected, reportPeer)
    room.on(RoomEvent.TrackSubscribed, onSubscribed)

    // TrackSubscribed only fires for tracks that arrive AFTER this subscription.
    // On a reconnect — or any remount where the room is already populated — the
    // remote tracks are already there, so first_media/stable_media would never
    // be reported for exactly the sessions most likely to be in trouble.
    // Reconcile what is already subscribed once, on mount.
    for (const participant of room.remoteParticipants.values()) {
      for (const publication of participant.trackPublications.values()) {
        const existing = publication.track
        if (publication.isSubscribed && existing) {
          onSubscribed(existing as RemoteTrack)
          // Only stop once something was actually reported: breaking
          // unconditionally would skip this participant's remaining
          // publications, so a camera track iterated before the mic could leave
          // first_media unreported — the exact gap this loop exists to close.
          if (reportedFirst.current) break
        }
      }
      if (reportedFirst.current) break
    }
    return () => {
      room.off(RoomEvent.ParticipantConnected, reportPeer)
      room.off(RoomEvent.TrackSubscribed, onSubscribed)
      if (stableTimer) clearTimeout(stableTimer)

      // Leaving after media started but before it was proven stable is a
      // FAILURE, not silence. Cancelling the timer without reporting made the
      // shortest, worst sessions — connect, see nothing usable, give up — the
      // ones the stable-media alarm never heard about.
      if (reportedFirst.current && !reportedStable.current) {
        reportedStable.current = true
        report(credential, {
          stage: 'stable_media',
          outcome: 'error',
          errorKind: 'left_before_stable',
          durationMs: Date.now() - connectedAt,
        })
      }
    }
  }, [room, credential])

  return null
}

/**
 * Reports whether the browser actually granted the devices. A blocked camera or
 * microphone is one of the most common real-world join failures, and it is
 * invisible to the server.
 */
export async function reportPermissions(credential: {
  code?: string | null
  reconnectToken?: string | null
}): Promise<void> {
  try {
    const permissions = (navigator as { permissions?: { query: (d: { name: string }) => Promise<{ state: string }> } })
      .permissions

    // Returning early when the Permissions API is missing threw away the ONE
    // report that proves a client is talking to us at all. Safari has no
    // queryable microphone permission, so on those devices the funnel went
    // completely quiet and looked identical to a broken transport. Report
    // `unknown` instead: an unknown state is a real answer, and this doubles as
    // the per-join telemetry heartbeat.
    const [camera, microphone] = permissions
      ? await Promise.all([
          permissions.query({ name: 'camera' }).catch(() => undefined),
          permissions.query({ name: 'microphone' }).catch(() => undefined),
        ])
      : [undefined, undefined]

    report(credential, {
      stage: 'permissions',
      outcome: microphone?.state === 'denied' ? 'error' : 'ok',
      permission: `camera=${camera?.state ?? 'unknown'},mic=${microphone?.state ?? 'unknown'}`,
    })
  } catch {
    /* advisory only */
  }
}

/**
 * Total inbound RTP bytes across all receivers, or undefined where getStats is
 * unavailable. Used as the honest measure of "is media still arriving".
 */
async function bytesReceived(room: unknown): Promise<number | undefined> {
  return (await readLinkHealth(room))?.inboundBytes
}

/**
 * How media actually reached this client: `host` (same network), `srflx`
 * (STUN-assisted direct), `relay` (TURN — someone's UDP was blocked), or
 * `prflx`. `unknown` when the browser gave us stats we could not interpret.
 *
 * This used to return the literal string `'subscriber'` whenever a subscriber
 * peer connection existed, which is true for every successful connection ever
 * made and therefore told us nothing — while the comment at the call site
 * claimed it distinguished relay from direct. Nine months of `ice=subscriber`
 * rows could not answer the one question they were collected for.
 *
 * Read from the SELECTED candidate pair. Chromium exposes it as a
 * `transport` stat pointing at `selectedCandidatePairId`; Firefox instead marks
 * the pair itself with `selected`, and older Chromium had `nominated` pairs with
 * no transport link — so all three are tried before giving up. Deliberately
 * best-effort and never throwing: this is advisory telemetry riding on internal
 * SDK shape, and a stats quirk must not cost a report.
 */
async function describeIce(room: unknown): Promise<string | undefined> {
  try {
    type Transport = { getStats?: () => Promise<RTCStatsReport> }
    const engine = (room as {
      engine?: { pcManager?: { subscriber?: Transport; publisher?: Transport } }
    }).engine

    // The subscriber carries inbound media, so it is the transport whose path we
    // most want — but it is CONDITIONAL and the publisher is not. Verified in the
    // installed SDK: PCTransportManager's constructor creates `publisher`
    // unconditionally and then `this.subscriber = new PCTransport(...)` only
    // `if (mode !== 'publisher-only')`, and livekit's own code optional-chains
    // `this.subscriber` everywhere it touches it.
    //
    // That is the most likely reason the previous version reported nothing at all
    // for a whole cohort: it read `pcManager.subscriber` and gave up when it was
    // absent. Falling back to the publisher gets us a candidate pair either way —
    // the local candidate type is what we are after, and on a bundled connection
    // both transports traverse the same network path.
    const pcManager = engine?.pcManager
    const transport = pcManager?.subscriber ?? pcManager?.publisher

    // NEVER a silent undefined.
    //
    // The previous version returned `undefined` whenever it could not reach the
    // transport, JSON.stringify dropped the key, and the server's `.compact`
    // dropped it again — so "we could not read the ICE path" was indistinguishable
    // from "this build does not report ICE at all". On 2026-08-03 that cost a
    // whole cohort: 9 first_media events arrived with no `ice` field and there was
    // no way to tell which of the two it was. Naming the reason turns an invisible
    // failure into a visible one, the same way the webhook gauge's 99999 sentinel
    // does server-side.
    if (!engine) return 'unavailable:no-engine'
    if (!pcManager) return 'unavailable:no-pcmanager'
    if (!transport?.getStats) return 'unavailable:no-getstats'

    const stats = await transport.getStats.call(transport)

    type Pair = {
      type?: string
      selected?: boolean
      nominated?: boolean
      state?: string
      localCandidateId?: string
      remoteCandidateId?: string
      protocol?: string
    }
    const pairs: Pair[] = []
    let selectedPairId: string | undefined
    const byId = new Map<string, { type?: string; candidateType?: string; protocol?: string }>()

    stats.forEach((entry, id) => {
      const row = entry as Pair & { selectedCandidatePairId?: string; candidateType?: string }
      byId.set(id, row)
      if (row.type === 'transport' && row.selectedCandidatePairId) {
        selectedPairId = row.selectedCandidatePairId
      }
      if (row.type === 'candidate-pair') pairs.push(row)
    })

    const pair =
      (selectedPairId ? (byId.get(selectedPairId) as Pair | undefined) : undefined) ??
      pairs.find((p) => p.selected) ??
      pairs.find((p) => p.nominated && p.state === 'succeeded') ??
      pairs.find((p) => p.state === 'succeeded')

    if (!pair?.localCandidateId) return 'unknown:no-selected-pair'
    const candidateType = byId.get(pair.localCandidateId)?.candidateType ?? 'unknown:no-candidate-type'
    const protocol = (
      byId.get(pair.localCandidateId)?.protocol ??
      (pair.remoteCandidateId ? byId.get(pair.remoteCandidateId)?.protocol : undefined) ??
      pair.protocol
    )?.toLowerCase()
    return protocol === 'udp' || protocol === 'tcp' ? `${candidateType},proto=${protocol}` : candidateType
  } catch {
    // Also named rather than dropped — a throw here is a different fact from a
    // missing transport, and both used to arrive as an absent field.
    return 'unavailable:threw'
  }
}
